const resultMedia = "/tutorial-media/english-year10-v3";
const setupMedia = "/tutorial-media/getting-started-v1";
const screenshot = (name, alt) => ({
  src: `${setupMedia}/${name}.png`,
  alt,
  caption:
    "Synthetic example shown in Fruitify. Names and work are illustrative.",
});
const resultScreenshot = (name, alt) => ({
  src: `${resultMedia}/${name}.png`,
  alt,
  caption: "Original synthetic Year 10 English example; no real student data.",
});
const budScreenshot = (name, alt) => ({
  src: `/tutorial-media/bud-creation-v1/${name}.png`,
  alt,
  caption:
    "Synthetic walkthrough of Fruitify. Generated content is illustrative; always review your own draft.",
});
const step = (id, title, body, image) => ({
  id,
  title,
  body,
  ...(image ? { screenshot: image } : {}),
});
const common = {
  status: "published",
  availability: "teacher",
  audiences: ["Teachers"],
  version: "1",
  reviewedRelease: "3.2.0",
  reviewedAt: "2026-09-21",
};

export const helpContent = [
  {
    ...common,
    id: "first-answer",
    slug: "first-answer",
    kind: "tutorial",
    title: "Getting started: your first assignment and marked answer",
    summary:
      "Set up your class, create an assignment, add questions and submit an answer. Follow the screenshots or jump to the submission method you need. All pictured data is synthetic.",
    outcome:
      "Go from a new assignment to feedback you can review and use in your next lesson.",
    keywords: [
      "getting started",
      "assignment",
      "upload",
      "handwritten",
      "document",
      "AI",
      "Question Hub",
      "students",
      "Wonde",
    ],
    action: "createAssignment",
    related: ["ask-bud", "marked-answer", "class-insights", "first-quiz"],
    version: "2",
    reviewedAt: "2026-09-24",
    steps: [
      step(
        "class",
        "Choose your class",
        "Open Classes and choose the class you want to teach. If you manage your own class data, create a class first and check its subject and year group. At a Wonde-synced school, use the classes supplied by your school; ask your school administrator if a class is missing.",
        screenshot(
          "class",
          "Class view with Create Assignment and the class-management control",
        ),
      ),
      step(
        "students",
        "Check the students, then return to teaching",
        "For your own class data, select the settings icon labelled Manage class and students. Add or manage students here so you can associate each answer with the right person. Select Back to class view to return to assignments. This switch is available for direct teacher accounts; it is not a way to edit a class list managed through Wonde.",
        screenshot(
          "class-students",
          "Class management view with student controls and Back to class view",
        ),
      ),
      step(
        "create",
        "Create an assignment",
        "Select Create Assignment from the class. Choose Create with Bud for a guided AI draft, Create a new assignment for the traditional editor, or Choose existing assignments to reuse an assignment. Reusing an assignment is different from adding individual existing questions to a new one.",
        budScreenshot(
          "assignment-choice",
          "Create assignment screen with traditional, Bud and existing-assignment options",
        ),
      ),
      step(
        "bud",
        "Create a written assignment with Bud",
        "In Create with Bud, confirm the class, choose a question count and due date, then provide each question’s topic, objectives and marks. Review the proposal and select Generate with Bud. Go to Assignment opens your draft for checking and publication. For the full guided walkthrough, open Create assignments and quizzes with Bud in Related guides. The next steps explain the traditional editor; both routes lead to an assignment you can submit answers to.",
        budScreenshot(
          "assignment-question",
          "Guided Bud assignment creation inside Fruitify",
        ),
      ),
      step(
        "details",
        "Using the traditional editor: complete the assignment details",
        "Enter an Assignment Name and Due Date. Check the class, subject and year group before adding questions. A clear name helps students recognise the work they need to submit.",
        screenshot(
          "assignment-details",
          "Assignment details including name, class and due date",
        ),
      ),
      step(
        "upload-questions",
        "Option 1: upload questions and mark schemes",
        "Choose Upload and let AI help to upload a document containing your questions and mark schemes. Follow the upload controls and wait for extraction to finish. Review the extracted wording, source material and mark scheme before using the questions; extraction still needs your judgement.",
        screenshot(
          "question-document",
          "Document extraction dialog for questions and mark schemes",
        ),
      ),
      step(
        "generate-questions",
        "Option 2: write questions or use AI",
        "Choose Create your own questions. Write your question and mark scheme, or use the available AI controls to help create them. Check the subject, level, wording and expected answer before saving. Include source material when the question refers to a passage. Review AI-generated content before setting it to students.",
        screenshot(
          "question-create",
          "Question creation dialog with manual and AI controls",
        ),
      ),
      step(
        "existing-questions",
        "Option 3: use Question Hub",
        "Select Choose existing questions to browse Question Hub. Select a suitable question and check its wording and mark scheme. Add it to your assignment, then review the selected questions and their order. You do not need to create every question from scratch.",
        screenshot("question-existing", "Question Hub selection dialog"),
      ),
      step(
        "publish",
        "Check and publish the assignment",
        "Check the questions, mark schemes, class and due date, then select Finish to publish when ready. Open the assignment and choose Review / Upload Answers for a question. Keep the question visible while checking that you are submitting the right work.",
        screenshot(
          "assignment-ready",
          "Published assignment with its questions and Review / Upload Answers controls",
        ),
      ),
      step(
        "upload-link",
        "Share an upload URL",
        "Use the upload-link or mobile-upload control for the assignment or question and copy its URL, or let students scan the QR code. Share the link with the intended class. When prompted, students select their own name before submitting. Keep the full link intact; if it is invalid or expired, check the due date and request a fresh link. The links pictured in this guide are examples and cannot accept work.",
        screenshot("upload-link", "Example upload link and QR-code dialog"),
      ),
      step(
        "typed-answer",
        "Type and format an answer",
        "Students can type directly after signing in or choose Typed on a shared submission link. Select your own name where prompted, then write the answer. Shared class links rely on students selecting their own name; anyone with the link can select another name and access its draft. Use bold, italic, underline or lists to organise it. Choose Save draft to save without sending it for marking. Changed work also saves automatically every 10 seconds; wait for Draft saved before leaving. Reopening the same question restores your draft and formatting. Choose Submit for marking when ready, and wait for confirmation. Saving does not count as a submission or extend the deadline. If a save fails, keep the page open and retry. If the draft changed in another session, check before reloading because reloading replaces unsaved edits. Formatting is preserved, while marking evaluates the answer text.",
      ),
      step(
        "typing-settings",
        "Choose student typing settings",
        "In your Account preferences, choose whether students can use spelling assistance and copy and paste. These choices become the defaults for new assignments. You can also adjust them when creating an assignment or later in Assignment Preferences. Changing your Account preferences does not affect existing assignments. The student editor shows icons indicating whether each option is enabled; hover over an icon for details. Disabling copy and paste blocks normal copying, cutting, pasting and text dropping in the student editor. Disabling spelling assistance asks the browser to turn off spell check and autocorrect, although device tools may still offer assistance. These settings apply to signed-in students and shared submission links. They do not affect teacher corrections, image uploads or document uploads.",
      ),
      step(
        "handwritten",
        "Submit photographs of a handwritten answer",
        "Open the shared link on a phone, or use the image-upload option available in the answer screen. Select the student where prompted. Photograph or choose clear images of every page, with the writing fully visible and in the correct order. Review the previews, remove unwanted images and confirm the upload. Wait for the submission confirmation before closing the page.",
        screenshot(
          "handwritten-upload",
          "Handwritten answer upload with image selection controls",
        ),
      ),
      step(
        "confirm-upload",
        "Confirm the upload",
        "Check the selected student and files before confirming. For image uploads, Submit opens a confirmation dialog. Confirm only when the selection is correct, then wait for the success message. Processing and marking follow submission; the result is not immediate.",
        screenshot("upload-confirm", "Image submission confirmation dialog"),
      ),
      step(
        "document",
        "Upload an answer document",
        "For an answer saved as a file, use the document-upload option and choose a supported PDF, DOC or DOCX file. Associate it with the correct student, review the selection and submit. Follow the controls for the screen you are using: a question-specific upload and an assignment-level upload have different scopes. Multi-student bulk upload is a separate feature and may depend on your subscription.",
        screenshot(
          "document-upload",
          "Answer document upload with student and file selection",
        ),
      ),
      step(
        "student-access",
        "Wonde-synced students can access work directly",
        "Students with accounts provided through your school's Wonde sync can sign in using the school's configured sign-in method and open their assigned work directly. They do not need the shared upload URL. Wonde supplies school data; it is not a login button. If a student cannot see the assignment, check their class membership and that the assignment is published.",
        screenshot(
          "assignment-signed-in",
          "Signed-in student assignment view with Review / Upload Answer controls",
        ),
      ),
      step(
        "review",
        "Review the first marked answer",
        "Processing can take time. Once feedback is ready, open the student's answer and check the response, mark and supporting feedback. Review the evidence before using or releasing the feedback. The related marked-answer example shows what this looks like; the class-insights example shows how to decide what to teach next.",
        resultScreenshot(
          "marked-answer/answer",
          "Student answer with evidence annotations and feedback",
        ),
      ),
    ],
  },
  {
    ...common,
    id: "first-quiz",
    slug: "first-quiz",
    kind: "tutorial",
    title: "Getting started: create your first quiz",
    summary:
      "Create or reuse a quiz, prepare questions, share it with students and use the results to plan your next lesson. Screenshots use synthetic data.",
    outcome:
      "Check understanding with a quiz and find the questions your class needs to revisit.",
    keywords: [
      "quiz",
      "quick create",
      "multiple choice",
      "AI",
      "student link",
      "question level analysis",
    ],
    action: "quizzes",
    related: ["ask-bud", "first-answer", "class-insights"],
    version: "2",
    reviewedAt: "2026-09-24",
    steps: [
      step(
        "create",
        "Choose a class and create a quiz",
        "Open Quizzes, or switch the class view from Assignments to Quizzes. Choose Create with Bud for guided AI creation, start a new quiz in the traditional editor, or choose an existing quiz. If you need to set up your own class or students first, follow the class steps in the first-answer guide.",
        budScreenshot(
          "quiz-choice",
          "Create quiz screen with traditional, Bud and existing-quiz options",
        ),
      ),
      step(
        "bud",
        "Create a quiz with Bud",
        "In Create with Bud, enter the topic, optional areas of focus and due date, then choose 2–50 multiple-choice and true/false questions in total. Generate with Bud prepares a draft; Go to Quiz opens it for checking and publication. Open Create assignments and quizzes with Bud in Related guides for the full walkthrough. The following steps cover the traditional editor and Quick create alternative.",
        budScreenshot(
          "quiz-counts",
          "Guided Bud quiz creation inside Fruitify",
        ),
      ),
      step(
        "details",
        "Using the traditional editor: complete the quiz details",
        "Enter a Quiz Name, description and Due Date, and select the class. Check the subject and year group. Describe the knowledge you want to check so the quiz has a clear purpose.",
        screenshot(
          "quiz-details",
          "Quiz details with class, name and due date",
        ),
      ),
      step(
        "questions",
        "Add and review questions",
        "Use Quick create to generate quiz questions with AI, upload a question document, or choose existing questions from Question Hub. Review the question text, answer options and correct answers. Remove or change unsuitable questions before publishing. The quiz question options differ from long-answer assignment questions.",
        screenshot(
          "quiz-question-options",
          "Quiz question-source options including AI Quick create",
        ),
      ),
      step(
        "quick-create",
        "Use AI Quick create",
        "In Quick create, enter the topic and any additional context. Choose how many multiple-choice and true/false questions you want, then select Create. Wait for generation to complete and review every question and correct answer before publishing.",
        screenshot(
          "quiz-generate",
          "Quiz generation dialog with topic and question counts",
        ),
      ),
      step(
        "publish",
        "Publish and share the quiz",
        "When the questions and details are ready, select Finish to publish the quiz. From Student Submissions, select Get Submission QR code to generate the student link and share it with your class. Students using the link select their own name when prompted. Wonde-synced students with school accounts can also sign in and access their assigned quizzes directly.",
        screenshot(
          "quiz-link",
          "Quiz sharing dialog with an illustrative student link",
        ),
      ),
      step(
        "complete",
        "Complete and submit the quiz",
        "Students choose their answers to the quiz questions, check their selections and submit. They should wait for confirmation before closing the page. This is an on-screen question-and-answer flow, not the handwritten-image or document-upload process used for assignments.",
        screenshot(
          "quiz-student",
          "Student quiz showing answer choices and submission control",
        ),
      ),
      step(
        "signed-in",
        "Direct access for school students",
        "Students with school accounts can open Quizzes after signing in and select their assigned quiz. This includes students supplied by Wonde sync. They answer the questions on screen without selecting a name from a shared-link list.",
        screenshot(
          "quiz-signed-in",
          "Signed-in student quiz with answer choices",
        ),
      ),
      step(
        "results",
        "Review submissions and results",
        "Open the published quiz to see who has submitted and who needs a reminder. Review individual submissions to understand the student's choices.",
        screenshot(
          "quiz-results",
          "Teacher view of a published quiz and submissions",
        ),
      ),
      step(
        "analysis",
        "Use question-level analysis",
        "Inspect the question-level analysis to find questions that many students answered incorrectly. Compare the answer choices to identify likely misconceptions, then revisit those ideas in class. Use results alongside your teaching judgement; a single quiz is only one piece of evidence.",
        screenshot(
          "quiz-analysis",
          "Question-level analysis showing correct and incorrect responses",
        ),
      ),
    ],
  },
  {
    ...common,
    video: {
      src: "/tutorial-media/english-year10-v3/marked-answer/tour.mp4",
      poster: "/tutorial-media/english-year10-v3/marked-answer/answer.png",
      captions: {
        src: "/tutorial-media/english-year10-v3/marked-answer/captions.vtt",
        language: "en",
        label: "English",
      },
      transcript:
        "Start in Assignments. Open an assessment to review the work your class has submitted.\n\nThe assessment brings together both questions, submission totals and the class average. Open Question 1 to review its answers.\n\nCheck the question and its source before reviewing a response. This example asks how language creates uncertainty.\n\nOpen the source extract from the question menu. Read the passage alongside the task so you can judge whether the student's interpretation is supported by the text.\n\nStudent Answers shows who has submitted, their marks and which responses you have already viewed. Open Alex Morgan’s response.\n\nStart with the overall result and feedback. Alex has selected evidence well; the next step is to explain the reader’s response more precisely.\n\nRead the response in context. Highlighted evidence connects the student’s words to the assessment objective and the teacher’s feedback.\n\nSelect an annotation to inspect the explanation and suggested next step. Review the evidence before deciding whether the mark and feedback are right.\n\nReturn to the assessment to continue reviewing the class. Individual feedback and the class summary help you decide what to teach next.",
    },
    id: "marked-answer",
    interactiveExample: "marked-answer",
    version: "2",
    reviewedAt: "2026-09-22",
    slug: "marked-answer",
    kind: "example",
    title: "See a marked answer and useful feedback",
    summary:
      "Explore Alex's original synthetic English response, select evidence and read its feedback, or follow the video and written walkthrough. The example illustrates a review workflow, not a promise of marking accuracy.",
    outcome:
      "See how a response, supporting evidence and feedback fit together.",
    keywords: ["marking", "feedback", "English", "annotations", "example"],
    action: "createAssignment",
    related: ["first-answer", "class-insights"],
    downloads: [
      {
        src: `${setupMedia}/sample-feedback.pdf`,
        label: "Read the sample feedback companion (PDF)",
        description:
          "Original synthetic work and illustrative feedback. This companion is not a backend-generated Fruitify export or an exam-board assessment.",
      },
    ],
    steps: [
      step(
        "task",
        "Understand the task",
        "The task asks how language creates uncertainty in an original extract about Maya waiting for a train. Read the passage and mark scheme before reviewing the response.",
        resultScreenshot(
          "marked-answer/source",
          "Source extract shown alongside the assignment",
        ),
      ),
      step(
        "feedback",
        "Read the feedback alongside the answer",
        "The illustrative mark for Question 1 is 6 out of 8 (the two-question assignment total is 11 out of 16). Alex selects relevant evidence and recognises personification. The feedback asks for deeper explanation of how individual words affect the reader.",
        resultScreenshot(
          "marked-answer/feedback",
          "Overall performance and feedback for the sample answer",
        ),
      ),
      step(
        "evidence",
        "Check the supporting evidence",
        "Follow an annotation back to the student's words. Naming a technique is a starting point; explaining its effect is the next step. Review whether the evidence supports the mark and feedback.",
        resultScreenshot(
          "marked-answer/evidence",
          "Selected evidence annotation and its explanation",
        ),
      ),
      step(
        "next",
        "Turn feedback into an action",
        "Ask Alex to develop the explanation of distant: how does it suggest both physical separation and uncertainty? Model one explanation, then ask for a revised paragraph. Use the first-answer guide to try this with your own assignment.",
      ),
    ],
  },
  {
    ...common,
    video: {
      src: "/tutorial-media/english-year10-v3/class-insights/tour.mp4",
      poster: "/tutorial-media/english-year10-v3/class-insights/feedback.png",
      captions: {
        src: "/tutorial-media/english-year10-v3/class-insights/captions.vtt",
        language: "en",
        label: "English",
      },
      transcript:
        "Open the assessment from Assignments. This example follows a Year 10 English class after a language-analysis task.\n\nCompare submission totals and the class average first. Here, 22 of 24 students have submitted both questions; two still need following up.\n\nRead the class-level feedback for patterns. The class selects evidence well, but many explanations stop after identifying a technique.\n\nOpen the learning evidence. Model how “sighed” changes the mood, then ask students to improve an explanation using the surrounding evidence.\n\nExplore a second teaching focus: the city lights can suggest both hope and distance. Compare interpretations before choosing the best-supported one.\n\nCheck the individual responses behind the summary. Open Question 1, then use the submission list to choose work to review.\n\nUse an individual example to test the class pattern. Alex’s response supports the same next step: develop how specific words affect the reader.",
    },
    id: "class-insights",
    slug: "class-insights",
    kind: "example",
    title: "Turn class results into next steps",
    summary:
      "Explore an original synthetic Year 10 English assessment. Start with submissions, then connect class feedback to individual answers and a teaching priority.",
    outcome:
      "Identify a useful next lesson from class results and supporting work.",
    keywords: [
      "class",
      "performance",
      "results",
      "insights",
      "teaching",
      "feedback",
    ],
    action: "assignments",
    related: ["marked-answer", "first-answer", "first-quiz"],
    leadScreenshot: resultScreenshot(
      "class-insights/feedback",
      "Class feedback highlighting strengths and areas for improvement",
    ),
    steps: [
      step(
        "submissions",
        "Check who has submitted",
        "In this synthetic example, 22 of 24 students have submitted both questions. Follow up the missing work before interpreting the class average as a picture of everyone.",
        resultScreenshot(
          "class-insights/assessment",
          "Assessment submission totals and class average",
        ),
      ),
      step(
        "patterns",
        "Look for a pattern",
        "The class selects quotations well, but many explanations stop after identifying a technique. Read class feedback with the underlying responses rather than treating a summary as a conclusion.",
        resultScreenshot(
          "class-insights/feedback",
          "Class feedback describing common strengths and gaps",
        ),
      ),
      step(
        "focus",
        "Choose a teaching focus",
        "Model how sighed changes the mood, then ask students to improve an explanation using the surrounding evidence. Explore how the city lights can suggest both hope and distance.",
        resultScreenshot(
          "class-insights/next-steps",
          "Expanded learning evidence supporting the next teaching step",
        ),
      ),
      step(
        "check",
        "Check individual work",
        "Open a student's response to test the pattern. Alex's answer supports the same next step: explain more precisely how word choices affect the reader. Use the related marked-answer example to inspect the detail.",
      ),
    ],
  },
  {
    ...common,
    id: "ask-bud",
    slug: "ask-bud",
    kind: "tutorial",
    title: "Create assignments and quizzes with Bud",
    summary:
      "Follow Bud’s guided creation, review an AI draft, or ask about work already in your class. Includes separate assignment and quiz walkthroughs.",
    outcome:
      "Create a draft you can check and publish yourself, and understand how Bud uses your class or assignment context.",
    keywords: [
      "Bud",
      "assistant",
      "conversation",
      "retry",
      "analysis",
      "create",
      "assignment",
      "quiz",
      "AI",
      "objectives",
      "mark scheme",
    ],
    related: ["first-answer", "first-quiz", "marked-answer", "class-insights"],
    version: "2",
    reviewedAt: "2026-09-24",
    steps: [
      step(
        "start",
        "Choose Create with Bud",
        "Use Create with Bud on the dashboard, or the Bud card on an assignment or quiz creation page. Use Bud to create is also available in the traditional creation form. Creation is a beta feature for teachers, including direct teacher accounts, within your existing creation limits. You can still use the traditional editor instead.\n\nBud uses the class already in context. If none is selected, choose Select class before continuing. Check the class name: its subject determines the assessment objectives offered for assignments. On smaller screens Bud opens as a full page; on desktop you can expand the chat if you want more room.",
        budScreenshot(
          "assignment-choice",
          "Assignment creation choices with the Create with Bud beta card",
        ),
      ),
      step(
        "assignment",
        "Assignment: choose the question count and due date",
        "Choose how many written questions you need (1–20) and a due date. Select Continue. Bud guides you through one question at a time; assignment creation currently uses written questions only.",
        budScreenshot(
          "assignment-settings",
          "Bud asking for written question count and a due date",
        ),
      ),
      step(
        "assignment-questions",
        "Assignment: describe each question and choose its marks",
        "Enter what this question should cover, including any particular text, theme or skill. Select the relevant assessment objectives and give each a mark allocation. These options come from the selected class’s subject. Complete both the instructions and marks before continuing.\n\nFor later questions you can reuse the previous question’s marking choices, then adjust them. Bud prepares the question wording and marking criteria; you remain responsible for checking them.",
        budScreenshot(
          "assignment-question",
          "A question’s instructions, subject objectives and mark allocation in Bud",
        ),
      ),
      step(
        "assignment-proposal",
        "Assignment: review, then Generate with Bud",
        "Select Review questions to check your choices, then Prepare proposal. Check the class, deadline and each question’s instructions and marks in the proposal. Select Generate with Bud when ready. This starts draft generation; it does not publish or share the assignment.",
        budScreenshot(
          "assignment-proposal",
          "Bud’s assignment proposal with Generate with Bud",
        ),
      ),
      step(
        "quiz",
        "Quiz: enter the topic, optional focus and due date",
        "Choose the quiz option in Create with Bud and confirm the class. Enter what the quiz is about and its due date. Particular areas of focus is optional: use it for themes, skills or misconceptions to target. It guides generation without becoming part of the quiz name. Select Continue. You can also start from an assignment’s Bud quiz control to use that work as context.",
        budScreenshot(
          "quiz-details",
          "Bud quiz topic, optional areas of focus and due date",
        ),
      ),
      step(
        "quiz-counts",
        "Quiz: choose the question types and generate",
        "Set the number of multiple-choice and true/false questions. Both start at zero; choose 2–50 questions in total. Either type can stay at zero. Select Generate with Bud to prepare the draft. The traditional quiz editor’s Quick create remains a separate option.",
        budScreenshot(
          "quiz-counts",
          "Bud quiz question counts and Generate with Bud",
        ),
      ),
      step(
        "generating",
        "Wait while Bud prepares the draft",
        "The generating indicator shows that work is still running. Avoid starting another request for the same assessment while it is being prepared. Generating a draft can take time; the conversation tracks the existing operation.",
        budScreenshot(
          "generating",
          "Bud’s draft card and Fruitify generating indicator",
        ),
      ),
      step(
        "review-draft",
        "Open the draft and publish from its page",
        "When the draft is ready, select Go to Assignment or Go to Quiz. Review the actual questions, marking criteria or correct answers, class and deadline there. Make any changes you need, then publish using the normal assessment controls. Bud does not publish the draft from this chat.\n\nShare it with students through your usual assignment or quiz route after publication. Creating the draft does not send students an email. The first-assignment and first-quiz guides below explain sharing, submissions and reviewing results.",
        budScreenshot("quiz-ready", "Ready quiz draft with Go to Quiz"),
      ),
      step(
        "context",
        "Ask about an existing assignment or class",
        "For analysis, open Bud from an assignment to ask about that work. Free analysis requires an assignment; Standard analysis stays within a selected class. Premium can compare classes you already have permission to access. Archived work is excluded. These analysis limits are separate from guided creation.",
      ),
      step(
        "question",
        "Ask a focused analysis question",
        "Choose a suggested question or type your own. For example, ask which areas need more practice. Review the evidence and feedback yourself before deciding what to teach next. Use a new creation conversation when you want Bud to draft an assignment or quiz.",
      ),
      step(
        "return",
        "Continue the conversation",
        "Reopen a conversation from Ask Bud to retain its assignment or class context and check its draft operations. Start another conversation for different work. Older conversations can be locked according to your account’s history allowance.",
      ),
      step(
        "retry",
        "Recover an interrupted response or generation",
        "If a response cannot be confirmed, select Retry last request. Fruitify checks whether the request already completed before trying again. Avoid sending the same request as a new message while the previous one is running.\n\nRefresh review updates a draft card’s saved status. If generation has failed and the card offers Retry generation, use that control for the existing draft operation. If an analysis question is too large, narrow it to a class or assignment.",
      ),
    ],
  },
];
